import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  type CodexCodeWorkflowSessionView,
  type ControlledShellPlanView,
  type HealthInfo,
  type WorkerSandboxStatusView,
} from "../api";
import { ChatView } from "./ChatView";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";

interface WorktreeRecord {
  id: string;
  branch: string;
  baseRef: string;
  path: string;
  createdAt: string;
  expiresAt: string;
}

export interface SimoTWorkspaceViewProps {
  onOpenAgentOs?: () => void;
}

interface DiffSummaryFile {
  path: string;
  additions: number;
  deletions: number;
  hunks: number;
}

export interface DiffSummary {
  files: DiffSummaryFile[];
  additions: number;
  deletions: number;
  hunks: number;
  lineCount: number;
}

interface CodeWorkflowStage {
  id: string;
  title: string;
  status: string;
  executionAllowed: false;
  evidenceRequired: boolean;
}

interface CodeWorkflowPlanResult {
  runId: string;
  approvalId: string;
  policyId: string;
  stages: CodeWorkflowStage[];
  gates: Record<string, string>;
  files: string[];
  testCommands: string[];
  branch: string;
  baseRef: string;
}

interface CommandLanePlanResult {
  command: string;
  runId: string;
  approvalId: string;
  policyId: string;
  auditId: number;
  boundary: ControlledShellPlanView["boundary"];
}

const CODE_WORKFLOW_PLAN_ENDPOINT = "/api/worktree/codex-code-workflow/plan";
const CONTROLLED_SHELL_PLAN_ENDPOINT = "/api/tools/controlled-shell/plan";

function gatePlanErrorMessage(response: unknown, endpoint: string, fallback: string): string {
  const record = response && typeof response === "object" ? (response as Record<string, unknown>) : null;
  const requestId = typeof record?.requestId === "string" ? record.requestId : null;
  const error = record && "error" in record ? record.error : response;
  let rawMessage: string;
  if (typeof error === "string") {
    rawMessage = error;
  } else if (error && typeof error === "object") {
    const errorRecord = error as Record<string, unknown>;
    const code = typeof errorRecord.code === "string" ? errorRecord.code : "";
    const message = typeof errorRecord.message === "string" ? errorRecord.message : "";
    rawMessage = [code, message].filter(Boolean).join(": ") || JSON.stringify(error);
  } else {
    rawMessage = fallback;
  }

  return [
    ownerFacingGlobalError(rawMessage || fallback),
    `Endpoint: ${endpoint}`,
    requestId ? `Request-ID ${requestId}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}

function simoTWorkspaceErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

type SimoTWorkspaceErrorContext = "status" | "diff" | "code-plan" | "command-plan";

const SIMOT_WORKSPACE_ERROR_CONTEXT: Record<SimoTWorkspaceErrorContext, { prefix: string; endpoint: string }> = {
  status: {
    prefix: "SimoT Workspace-Status konnte nicht geladen werden.",
    endpoint: "/api/worktree + /api/worktree/codex-code-workflow/sessions + /api/worker-sandbox/status + /api/health",
  },
  diff: { prefix: "Worktree-Diff konnte nicht geladen werden.", endpoint: "/api/worktree/:id/diff" },
  "code-plan": { prefix: "Code-Workflow-Plan konnte nicht erstellt werden.", endpoint: CODE_WORKFLOW_PLAN_ENDPOINT },
  "command-plan": { prefix: "Command-Gate-Plan konnte nicht erstellt werden.", endpoint: CONTROLLED_SHELL_PLAN_ENDPOINT },
};

function simoTWorkspaceErrorEvidence(copy: string, context: SimoTWorkspaceErrorContext): string {
  const info = SIMOT_WORKSPACE_ERROR_CONTEXT[context];
  let text = copy
    .replace(/\s*Authorization(?:[:\s]+Bearer)?(?:[:\s]+[A-Za-z0-9._~+/=-]+)*/gi, "")
    .replace(/\s*Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "")
    .replace(/\s*stack trace\b.*$/i, "")
    .replace(
      /\s*Endpoint im Fehler nicht enthalten; bitte Session erneuern oder Live-Daten neu laden\./,
      ". Bitte Session erneuern oder Live-Daten neu laden.",
    )
    .trim();
  if (!text.startsWith(info.prefix)) text = `${info.prefix} ${text}`;
  if (!/Endpoint:/i.test(text)) text = `${text} Endpoint: ${info.endpoint}.`;
  return text;
}

export function simoTWorkspaceErrorCopy(error: unknown, context: SimoTWorkspaceErrorContext = "status"): string {
  const text = simoTWorkspaceErrorText(error);
  if (/worktree.*not found|workspace.*not found|session.*not found|invalid.*worktree|stale.*workspace|worktree_state/i.test(text)) {
    return simoTWorkspaceErrorEvidence(
      "Workspace-Status ist nicht mehr aktuell. Bitte Worktrees und Agent-OS-Session neu laden.",
      context,
    );
  }
  return simoTWorkspaceErrorEvidence(ownerFacingGlobalError(text), context);
}

export function simoTWorkspaceStatusErrorCopy(error: unknown): string {
  return simoTWorkspaceErrorCopy(error, "status");
}

export function summarizeUnifiedDiff(raw: string): DiffSummary {
  const summary: DiffSummary = { files: [], additions: 0, deletions: 0, hunks: 0, lineCount: 0 };
  if (!raw.trim()) return summary;

  const lines = raw.split(/\r?\n/);
  summary.lineCount = lines.length;
  let current: DiffSummaryFile | null = null;

  for (const line of lines) {
    if (line.startsWith("diff --git ")) {
      const match = /diff --git a\/(.+?) b\/(.+?)$/.exec(line);
      current = { path: match?.[2] ?? "unbekannte-datei", additions: 0, deletions: 0, hunks: 0 };
      summary.files.push(current);
      continue;
    }

    if (!current) continue;
    if (line.startsWith("@@")) {
      current.hunks += 1;
      summary.hunks += 1;
    } else if (line.startsWith("+") && !line.startsWith("+++")) {
      current.additions += 1;
      summary.additions += 1;
    } else if (line.startsWith("-") && !line.startsWith("---")) {
      current.deletions += 1;
      summary.deletions += 1;
    }
  }

  return summary;
}

function shortPath(path: string | null | undefined): string {
  if (!path) return "nicht konfiguriert";
  const parts = path.split(/[\\/]+/).filter(Boolean);
  return parts.length > 3 ? `.../${parts.slice(-3).join("/")}` : path;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("de-DE");
  } catch {
    return iso;
  }
}

function timeUntil(iso: string): string {
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins} min`;
    return `${Math.round(mins / 60)} h`;
  } catch {
    return "?";
  }
}

function StatusPill({ tone, children }: { tone: "ok" | "warn" | "muted"; children: React.ReactNode }): JSX.Element {
  const classes =
    tone === "ok"
      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100"
      : tone === "warn"
        ? "border-amber-400/40 bg-amber-400/10 text-amber-100"
        : "border-jarvis-border bg-jarvis-bg/55 text-jarvis-muted";
  return <span className={`rounded border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${classes}`}>{children}</span>;
}

function WorkspaceMetric({ label, value, detail }: { label: string; value: string | number; detail: string }): JSX.Element {
  return (
    <div className="rounded-md border border-jarvis-border/80 bg-jarvis-bg/45 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">{label}</div>
      <div className="mt-1 truncate font-mono text-lg text-jarvis-text">{value}</div>
      <div className="mt-1 truncate text-[11px] text-jarvis-muted">{detail}</div>
    </div>
  );
}

export function SimoTWorkspaceRail({
  worktrees,
  codeSessions,
  workerSandbox,
  health,
  loading,
  error,
  onRefresh,
  onOpenAgentOs,
}: {
  worktrees: WorktreeRecord[];
  codeSessions: CodexCodeWorkflowSessionView[];
  workerSandbox: WorkerSandboxStatusView | null;
  health: HealthInfo | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void | Promise<void>;
  onOpenAgentOs?: () => void;
}): JSX.Element {
  const [codeGoal, setCodeGoal] = useState("");
  const [codeBranch, setCodeBranch] = useState("work/simot-code-session");
  const [codeFiles, setCodeFiles] = useState("apps/ui/src");
  const [codeTests, setCodeTests] = useState("corepack pnpm test");
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planResult, setPlanResult] = useState<CodeWorkflowPlanResult | null>(null);
  const [commandPlanningKey, setCommandPlanningKey] = useState<string | null>(null);
  const [commandPlanError, setCommandPlanError] = useState<string | null>(null);
  const [commandPlanResult, setCommandPlanResult] = useState<Record<string, CommandLanePlanResult>>({});
  const [selectedDiffWorktreeId, setSelectedDiffWorktreeId] = useState<string>("");
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffResult, setDiffResult] = useState<{
    worktreeId: string;
    branch: string;
    commit: string;
    baseRef: string;
    summary: DiffSummary;
    preview: string[];
  } | null>(null);
  const proposalWorker = workerSandbox?.workers.find((worker) => worker.id === "code_propose");
  const worktreeReady = workerSandbox?.worktree.configured === true && Boolean(workerSandbox.worktree.root);
  const safeWriteGate =
    workerSandbox?.enforcement.requiresWorktreeForWrites === true &&
    workerSandbox.enforcement.requiresApprovalForWrites === true &&
    workerSandbox.enforcement.requiresCleanApplyToSourceGate === true;
  const codeModel = health?.ollama.models.find((model) => /coder|code/i.test(model)) ?? null;

  const sortedWorktrees = useMemo(
    () => [...worktrees].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 4),
    [worktrees],
  );
  const sortedCodeSessions = useMemo(
    () => [...codeSessions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 4),
    [codeSessions],
  );
  const selectedDiffWorktree = useMemo(
    () => sortedWorktrees.find((item) => item.id === selectedDiffWorktreeId) ?? sortedWorktrees[0] ?? null,
    [selectedDiffWorktreeId, sortedWorktrees],
  );
  const splitList = (value: string): string[] =>
    value
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  const canPlan = codeGoal.trim().length > 0 && codeBranch.trim().length > 0 && splitList(codeFiles).length > 0 && splitList(codeTests).length > 0;

  useEffect(() => {
    if (!selectedDiffWorktreeId && sortedWorktrees[0]) {
      setSelectedDiffWorktreeId(sortedWorktrees[0].id);
    }
  }, [selectedDiffWorktreeId, sortedWorktrees]);

  const loadDiff = async (): Promise<void> => {
    if (!selectedDiffWorktree) return;
    setDiffLoading(true);
    setDiffError(null);
    setDiffResult(null);
    try {
      const res = await api.getWorktreeDiff(selectedDiffWorktree.id);
      const lines = res.diff.split(/\r?\n/).filter((line) => line.trim().length > 0);
      setDiffResult({
        worktreeId: selectedDiffWorktree.id,
        branch: selectedDiffWorktree.branch,
        commit: res.commit,
        baseRef: res.baseRef,
        summary: summarizeUnifiedDiff(res.diff),
        preview: lines.slice(0, 10),
      });
    } catch (err) {
      setDiffError(simoTWorkspaceErrorCopy(err instanceof Error ? err : "worktree_diff_failed", "diff"));
    } finally {
      setDiffLoading(false);
    }
  };

  const planCodeSession = async (): Promise<void> => {
    if (!canPlan) return;
    setPlanning(true);
    setPlanError(null);
    setPlanResult(null);
    const goal = codeGoal.trim();
    try {
      const res = await api.planCodexCodeWorkflow({
        goal,
        branch: codeBranch.trim(),
        baseRef: "feat/simo-personal-assistant-stability-1",
        files: splitList(codeFiles),
        testCommands: splitList(codeTests),
        prTitle: `SimoT: ${goal.slice(0, 80)}`,
        prBody: `Ziel:\n${goal}\n\nGates bleiben aktiv.`,
      });
      if ("ok" in res && res.ok) {
        setPlanResult({
          runId: res.runId,
          approvalId: res.approval.id,
          policyId: res.policyId,
          stages: res.workflow.stages,
          gates: res.gates,
          files: res.workflow.files,
          testCommands: res.workflow.testCommands,
          branch: res.workflow.branch,
          baseRef: res.workflow.baseRef,
        });
        void onRefresh();
      } else {
        setPlanError(gatePlanErrorMessage(res, CODE_WORKFLOW_PLAN_ENDPOINT, "codex_code_workflow_plan_failed"));
      }
    } catch (err) {
      setPlanError(simoTWorkspaceErrorCopy(err instanceof Error ? err : "codex_code_workflow_plan_failed", "code-plan"));
    } finally {
      setPlanning(false);
    }
  };

  const planSessionCommand = async (session: CodexCodeWorkflowSessionView, command: string): Promise<void> => {
    const key = `${session.runId}:${command}`;
    setCommandPlanningKey(key);
    setCommandPlanError(null);
    try {
      const res = await api.planControlledShell({
        command,
        reason: `SimoT Agent OS Session ${session.runId}: ${session.goal}`,
        expectedOutput: "Command is planned for controlled-shell approval; no automatic execution.",
      });
      if ("ok" in res && res.ok) {
        setCommandPlanResult((previous) => ({
          ...previous,
          [key]: {
            command: res.command,
            runId: res.runId,
            approvalId: res.approval.id,
            policyId: res.policyId,
            auditId: res.evidence.auditId,
            boundary: res.boundary,
          },
        }));
        void onRefresh();
      } else {
        setCommandPlanError(gatePlanErrorMessage(res, CONTROLLED_SHELL_PLAN_ENDPOINT, "controlled_shell_plan_failed"));
      }
    } catch (err) {
      setCommandPlanError(simoTWorkspaceErrorCopy(err instanceof Error ? err : "controlled_shell_plan_failed", "command-plan"));
    } finally {
      setCommandPlanningKey(null);
    }
  };

  const renderCommandLane = (session: CodexCodeWorkflowSessionView): JSX.Element => (
    <div className="mt-3 rounded-md border border-jarvis-border/60 bg-jarvis-bg/40 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-jarvis-muted">Command/Test Lane</div>
          <div className="mt-1 truncate text-[11px] text-jarvis-muted">Shell nur als Approval-Plan</div>
        </div>
        <StatusPill tone="warn">No direct run</StatusPill>
      </div>
      {session.testCommands.length === 0 ? (
        <div className="mt-2 rounded border border-dashed border-jarvis-border p-2 text-[11px] text-jarvis-muted">
          Keine Testbefehle fuer diese Session.
        </div>
      ) : (
        <ul className="mt-2 space-y-2">
          {session.testCommands.slice(0, 3).map((command) => {
            const key = `${session.runId}:${command}`;
            const planned = commandPlanResult[key];
            const isPlanning = commandPlanningKey === key;
            return (
              <li key={key} className="rounded border border-jarvis-border/60 bg-jarvis-surface/35 p-2">
                <div className="min-w-0 truncate font-mono text-[10px] text-jarvis-text" title={command}>
                  {command}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="truncate text-[10px] uppercase tracking-[0.14em] text-jarvis-muted">
                    SIMOT-CONTROLLED-SHELL-ACTION-GATED-1
                  </span>
                  <button
                    type="button"
                    onClick={() => void planSessionCommand(session, command)}
                    disabled={isPlanning}
                    className="shrink-0 rounded-md border border-jarvis-accent/40 bg-jarvis-accent/15 px-2 py-1 text-[11px] text-jarvis-text hover:bg-jarvis-accent/25 disabled:cursor-not-allowed disabled:border-jarvis-border disabled:bg-jarvis-bg/45 disabled:text-jarvis-muted"
                  >
                    {isPlanning ? "Plant..." : "Gate planen"}
                  </button>
                </div>
                {planned && (
                  <div className="mt-2 grid grid-cols-1 gap-1.5 text-[10px] text-jarvis-muted sm:grid-cols-2 lg:grid-cols-1">
                    <div className="truncate rounded border border-emerald-400/25 bg-emerald-400/10 px-2 py-1">
                      Run <span className="font-mono text-emerald-100">{planned.runId.slice(0, 12)}</span>
                    </div>
                    <div className="truncate rounded border border-emerald-400/25 bg-emerald-400/10 px-2 py-1">
                      Approval <span className="font-mono text-emerald-100">{planned.approvalId}</span>
                    </div>
                    <div className="truncate rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                      Evidence <span className="font-mono text-jarvis-text">audit:{planned.auditId}</span>
                    </div>
                    <div className="truncate rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                      Boundary <span className="font-mono text-jarvis-text">{planned.boundary.shellAllowedWithoutApproval ? "open" : "approval-first"}</span>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {commandPlanError && (
        <div className="mt-2 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-[11px] text-red-100">
          {ownerFacingGlobalError(commandPlanError)}
        </div>
      )}
    </div>
  );

  return (
    <aside className="flex min-h-0 min-w-0 w-full flex-col overflow-hidden border-t border-jarvis-border bg-jarvis-surface/70 lg:border-l lg:border-t-0">
      <div className="flex min-w-0 shrink-0 items-center gap-2 border-b border-jarvis-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-jarvis-muted">Agent OS Workspace</div>
          <h2 className="truncate text-sm font-semibold text-jarvis-text">SimoT arbeitet in Worktrees</h2>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="topbar-control inline-flex min-w-[42px] items-center justify-center px-0 text-jarvis-muted hover:text-jarvis-text"
          title="Workspace aktualisieren"
          aria-label="Workspace aktualisieren"
        >
          ↻
        </button>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 simo-thin-scroll">
        <div className="grid grid-cols-2 gap-2">
          <WorkspaceMetric label="Worktrees" value={worktrees.length} detail={worktreeReady ? "isolierte Code-Änderungen" : "Root fehlt"} />
          <WorkspaceMetric label="Worker" value={proposalWorker?.available ? "ready" : "gated"} detail={proposalWorker?.mode ?? "nicht geladen"} />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <StatusPill tone={worktreeReady ? "ok" : "warn"}>{worktreeReady ? "Worktree Root" : "Root fehlt"}</StatusPill>
          <StatusPill tone={safeWriteGate ? "ok" : "warn"}>{safeWriteGate ? "Approval Gate" : "Gate prüfen"}</StatusPill>
          <StatusPill tone={workerSandbox?.runtimeHardening.execution.dryRunOnly ? "warn" : "muted"}>
            {workerSandbox?.runtimeHardening.execution.dryRunOnly ? "Dry-run" : "Runtime"}
          </StatusPill>
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-red-400/40 bg-red-500/10 p-3 text-xs text-red-100">
            {ownerFacingGlobalError(error)}
          </div>
        )}

        <section className="simo-keyboard-safe-editor mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3" aria-label="SimoT Patch Iteration Keyboard-safe Plan">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Model Runtime</div>
              <div className="truncate text-xs text-jarvis-muted">Eigene Modelle via Ollama</div>
            </div>
            <StatusPill tone={health?.ollama.ok ? "ok" : health ? "warn" : "muted"}>
              {health ? (health.ollama.ok ? "online" : "offline") : "lade"}
            </StatusPill>
          </div>
          <div className="mt-3 space-y-2 text-xs">
            <div>
              <div className="text-jarvis-muted">Server</div>
              <div className="truncate font-mono text-jarvis-text" title={health?.ollama.baseUrl ?? undefined}>
                {health?.ollama.baseUrl ?? "lade..."}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <WorkspaceMetric label="Modelle" value={health?.ollama.models.length ?? "-"} detail="vom Server gelesen" />
              <WorkspaceMetric label="Code Modell" value={codeModel ?? "-"} detail={codeModel ? "vom Server" : "nicht gemeldet"} />
            </div>
            {health && health.ollama.models.length > 0 ? (
              <ul className="max-h-28 space-y-1 overflow-y-auto pr-1 simo-thin-scroll">
                {health.ollama.models.slice(0, 8).map((model) => (
                  <li key={model} className="truncate rounded border border-jarvis-border/60 bg-jarvis-surface/35 px-2 py-1 font-mono text-[11px] text-jarvis-text" title={model}>
                    {model}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-md border border-dashed border-jarvis-border p-2 text-[11px] text-jarvis-muted">
                Keine Modelle vom Server gemeldet.
              </div>
            )}
          </div>
        </section>

        <section className="simo-keyboard-safe-editor mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3" aria-label="SimoT Interactive Diff Keyboard-safe Worktree Select">
          <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Repository</div>
          <div className="mt-2 space-y-2 text-xs">
            <div>
              <div className="text-jarvis-muted">Source</div>
              <div className="truncate font-mono text-jarvis-text" title={workerSandbox?.sourceRepo.path ?? undefined}>
                {shortPath(workerSandbox?.sourceRepo.path)}
              </div>
            </div>
            <div>
              <div className="text-jarvis-muted">Worktree Root</div>
              <div className="truncate font-mono text-jarvis-text" title={workerSandbox?.worktree.root ?? undefined}>
                {shortPath(workerSandbox?.worktree.root)}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Aktive Branches</div>
              <div className="text-xs text-jarvis-muted">{loading ? "lade..." : `${worktrees.length} offen`}</div>
            </div>
            <button
              type="button"
              onClick={onOpenAgentOs}
              className="rounded-md border border-jarvis-accent/40 bg-jarvis-accent/15 px-3 py-2 text-xs text-jarvis-text hover:bg-jarvis-accent/25"
            >
              Agent OS öffnen
            </button>
          </div>

          {sortedWorktrees.length === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-jarvis-border p-3 text-xs text-jarvis-muted">
              Keine aktiven Worktrees. Starte eine Coding-Aufgabe in der Mission Console oder lege dort einen gated Branch an.
            </div>
          ) : (
            <ul className="mt-3 space-y-2">
              {sortedWorktrees.map((item) => (
                <li key={item.id} className="rounded-md border border-jarvis-border/70 bg-jarvis-surface/45 p-2">
                  <div className="truncate font-mono text-xs text-jarvis-text" title={item.branch}>
                    {item.branch}
                  </div>
                  <div className="mt-1 flex min-w-0 items-center gap-2 text-[11px] text-jarvis-muted">
                    <span className="truncate">{item.baseRef}</span>
                    <span aria-hidden="true">·</span>
                    <span>{timeUntil(item.expiresAt)}</span>
                  </div>
                  <div className="mt-1 truncate text-[10px] text-jarvis-muted" title={item.path}>
                    {shortPath(item.path)}
                  </div>
                  <div className="sr-only">erstellt {formatDate(item.createdAt)}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Interactive Diff Loop</div>
              <div className="mt-1 text-xs text-jarvis-muted">Review in SimoT, Apply bleibt im Agent-OS-Gate.</div>
            </div>
            <StatusPill tone="ok">No direct apply</StatusPill>
          </div>

          <div className="mt-3 space-y-2">
            <label className="block text-[11px] uppercase tracking-[0.14em] text-jarvis-muted" htmlFor="simot-diff-worktree">
              Worktree
            </label>
            <select
              id="simot-diff-worktree"
              name="simot-diff-worktree"
              value={selectedDiffWorktree?.id ?? ""}
              onChange={(event) => setSelectedDiffWorktreeId(event.target.value)}
              disabled={sortedWorktrees.length === 0}
              className="simo-keyboard-safe-field min-h-10 w-full rounded-md border border-jarvis-border bg-jarvis-bg/70 px-3 py-2 font-mono text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 disabled:text-jarvis-muted sm:text-xs"
            >
              {sortedWorktrees.length === 0 ? (
                <option value="">kein Worktree</option>
              ) : (
                sortedWorktrees.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.branch}
                  </option>
                ))
              )}
            </select>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => void loadDiff()}
                disabled={!selectedDiffWorktree || diffLoading}
                className="rounded-md border border-jarvis-accent/40 bg-jarvis-accent/15 px-3 py-2 text-xs text-jarvis-text hover:bg-jarvis-accent/25 disabled:cursor-not-allowed disabled:border-jarvis-border disabled:bg-jarvis-bg/45 disabled:text-jarvis-muted"
              >
                {diffLoading ? "Lädt..." : "Diff laden"}
              </button>
              <button
                type="button"
                onClick={onOpenAgentOs}
                className="rounded-md border border-jarvis-border bg-jarvis-bg/60 px-3 py-2 text-xs text-jarvis-muted hover:text-jarvis-text"
              >
                Mission Console öffnen
              </button>
            </div>
          </div>

          {diffError && (
            <div className="mt-3 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-[11px] text-red-100">
              {ownerFacingGlobalError(diffError)}
            </div>
          )}

          {diffResult ? (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <WorkspaceMetric label="Files" value={diffResult.summary.files.length} detail="geändert" />
                <WorkspaceMetric label="+" value={diffResult.summary.additions} detail="additions" />
                <WorkspaceMetric label="-" value={diffResult.summary.deletions} detail="deletions" />
              </div>
              <div className="rounded-md border border-jarvis-border/70 bg-jarvis-surface/45 p-2 text-[11px] text-jarvis-muted">
                <div className="truncate font-mono text-jarvis-text" title={diffResult.branch}>
                  {diffResult.branch}
                </div>
                <div className="mt-1 truncate">Base {diffResult.baseRef}</div>
                <div className="mt-1 truncate">Commit {diffResult.commit}</div>
                <div className="mt-1">Hunks {diffResult.summary.hunks} · Lines {diffResult.summary.lineCount}</div>
              </div>
              {diffResult.summary.files.length > 0 ? (
                <ul className="max-h-36 space-y-1 overflow-y-auto pr-1 simo-thin-scroll">
                  {diffResult.summary.files.slice(0, 6).map((file) => (
                    <li key={file.path} className="rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1 text-[11px]">
                      <div className="truncate font-mono text-jarvis-text" title={file.path}>
                        {file.path}
                      </div>
                      <div className="mt-1 text-jarvis-muted">
                        +{file.additions} / -{file.deletions} · {file.hunks} hunks
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-md border border-dashed border-jarvis-border p-2 text-[11px] text-jarvis-muted">
                  Keine Diff-Zeilen im Worktree.
                </div>
              )}
              {diffResult.preview.length > 0 && (
                <pre className="max-h-36 overflow-auto rounded-md border border-jarvis-border/70 bg-black/25 p-2 font-mono text-[10px] leading-relaxed text-jarvis-muted simo-thin-scroll">
                  {diffResult.preview.join("\n")}
                </pre>
              )}
            </div>
          ) : (
            <div className="mt-3 rounded-md border border-dashed border-jarvis-border p-3 text-xs text-jarvis-muted">
              Lade einen Worktree-Diff, prüfe die Dateien hier und öffne danach den gated Code-Review für Apply.
            </div>
          )}
        </section>

        <section className="mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Agent OS Session History</div>
              <div className="mt-1 text-xs text-jarvis-muted">wiederaufnehmbare SimoT Agent OS Sessions</div>
            </div>
            <StatusPill tone={sortedCodeSessions.length > 0 ? "ok" : "muted"}>{sortedCodeSessions.length}</StatusPill>
          </div>

          {sortedCodeSessions.length === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-jarvis-border p-3 text-xs text-jarvis-muted">
              Noch keine Agent-OS-Session geplant. Starte unten eine gated Session; sie erscheint danach hier mit Run, Approval und Evidence.
            </div>
          ) : (
            <ol className="mt-3 space-y-2" aria-label="Agent OS Session History">
              {sortedCodeSessions.map((session) => (
                <li key={session.runId} className="rounded-md border border-jarvis-border/70 bg-jarvis-surface/45 p-2 text-[11px]">
                  <div className="flex min-w-0 items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold text-jarvis-text" title={session.goal}>
                        {session.goal}
                      </div>
                      <div className="mt-1 truncate font-mono text-jarvis-muted" title={session.branch}>
                        {session.branch}
                      </div>
                    </div>
                    <span className="shrink-0 rounded border border-jarvis-border bg-jarvis-bg/55 px-2 py-1 font-mono text-[10px] text-jarvis-muted">
                      {session.approvalStatus}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5">
                    <div className="truncate rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                      Run <span className="font-mono text-jarvis-text">{session.runId.slice(0, 12)}</span>
                    </div>
                    <div className="truncate rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                      Evidence <span className="font-mono text-jarvis-text">{session.evidenceRef}</span>
                    </div>
                    <div className="truncate rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                      Files <span className="font-mono text-jarvis-text">{session.files.length}</span>
                    </div>
                    <div className="truncate rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                      Tests <span className="font-mono text-jarvis-text">{session.testCommands.length}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {session.stages.slice(0, 4).map((stage) => (
                      <span key={stage.id} className="rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1 text-[10px] text-jarvis-muted">
                        {stage.title}
                      </span>
                    ))}
                  </div>
                  {renderCommandLane(session)}
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Patch Iteration</div>
              <div className="mt-1 truncate text-xs text-jarvis-muted">SimoT Agent OS Session</div>
            </div>
            <StatusPill tone="warn">Approval first</StatusPill>
          </div>
          <div className="mt-3 space-y-2">
            <label className="block text-[11px] uppercase tracking-[0.14em] text-jarvis-muted" htmlFor="simot-code-session-goal">
              Ziel
            </label>
            <textarea
              id="simot-code-session-goal"
              name="simot-code-session-goal"
              value={codeGoal}
              onChange={(event) => setCodeGoal(event.target.value)}
              rows={3}
              className="simo-keyboard-safe-field w-full resize-y rounded-md border border-jarvis-border bg-jarvis-bg/70 px-3 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-xs"
              placeholder="SimoT Code-Aufgabe"
            />
            <label className="block text-[11px] uppercase tracking-[0.14em] text-jarvis-muted" htmlFor="simot-code-session-branch">
              Branch
            </label>
            <input
              id="simot-code-session-branch"
              name="simot-code-session-branch"
              value={codeBranch}
              onChange={(event) => setCodeBranch(event.target.value)}
              className="simo-keyboard-safe-field min-h-10 w-full rounded-md border border-jarvis-border bg-jarvis-bg/70 px-3 py-2 font-mono text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-xs"
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
              <label className="block text-[11px] uppercase tracking-[0.14em] text-jarvis-muted" htmlFor="simot-code-session-files">
                Dateien
                <input
                  id="simot-code-session-files"
                  name="simot-code-session-files"
                  value={codeFiles}
                  onChange={(event) => setCodeFiles(event.target.value)}
                  className="simo-keyboard-safe-field mt-1 min-h-10 w-full rounded-md border border-jarvis-border bg-jarvis-bg/70 px-3 py-2 font-mono text-base normal-case tracking-normal text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-xs"
                />
              </label>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-jarvis-muted" htmlFor="simot-code-session-tests">
                Tests
                <input
                  id="simot-code-session-tests"
                  name="simot-code-session-tests"
                  value={codeTests}
                  onChange={(event) => setCodeTests(event.target.value)}
                  className="simo-keyboard-safe-field mt-1 min-h-10 w-full rounded-md border border-jarvis-border bg-jarvis-bg/70 px-3 py-2 font-mono text-base normal-case tracking-normal text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-xs"
                />
              </label>
            </div>
            <button
              type="button"
              onClick={() => void planCodeSession()}
              disabled={!canPlan || planning}
              className="min-h-10 w-full rounded-md border border-jarvis-accent/40 bg-jarvis-accent/15 px-3 py-2 text-xs text-jarvis-text hover:bg-jarvis-accent/25 disabled:cursor-not-allowed disabled:border-jarvis-border disabled:bg-jarvis-bg/45 disabled:text-jarvis-muted"
            >
              {planning ? "Plant..." : "Plan gated session"}
            </button>
          </div>
          {planResult && (
            <div className="mt-3 space-y-3">
              <div className="rounded-md border border-emerald-400/30 bg-emerald-400/10 p-2 text-[11px] text-emerald-100">
                <div className="font-mono">Run {planResult.runId.slice(0, 12)}</div>
                <div className="truncate">Approval {planResult.approvalId}</div>
                <div className="truncate">{planResult.policyId}</div>
              </div>
              <div className="rounded-md border border-jarvis-border/70 bg-jarvis-surface/45 p-2 text-[11px] text-jarvis-muted">
                <div className="truncate font-mono text-jarvis-text" title={planResult.branch}>
                  {planResult.branch}
                </div>
                <div className="mt-1 truncate">Base {planResult.baseRef}</div>
                <div className="mt-1 truncate">Files {planResult.files.join(", ")}</div>
                <div className="mt-1 truncate">Tests {planResult.testCommands.join(", ")}</div>
              </div>
              <ol className="space-y-1.5" aria-label="Patch Iteration Stages">
                {planResult.stages.map((stage, index) => (
                  <li key={stage.id} className="rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1.5 text-[11px]">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-jarvis-text">
                          {index + 1}. {stage.title}
                        </div>
                        <div className="mt-1 truncate text-jarvis-muted">{stage.status}</div>
                      </div>
                      <span className="shrink-0 font-mono text-jarvis-muted">{stage.evidenceRequired ? "evidence" : "optional"}</span>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="grid grid-cols-1 gap-1.5 text-[11px] sm:grid-cols-2 lg:grid-cols-1">
                {Object.entries(planResult.gates).map(([key, value]) => (
                  <div key={key} className="flex min-w-0 justify-between gap-2 rounded border border-jarvis-border/60 bg-jarvis-bg/50 px-2 py-1">
                    <span className="truncate text-jarvis-muted">{key}</span>
                    <span className="truncate font-mono text-jarvis-text">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {planError && (
            <div className="mt-3 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-[11px] text-red-100">
              {ownerFacingGlobalError(planError)}
            </div>
          )}
        </section>

        <section className="mt-4 rounded-md border border-jarvis-border/80 bg-jarvis-bg/35 p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-jarvis-muted">Worker Gates</div>
          <div className="mt-2 space-y-1.5 text-xs text-jarvis-muted">
            <div className="flex justify-between gap-3">
              <span>Host Writes</span>
              <span className="font-mono text-jarvis-text">{workerSandbox?.directHostWriteAllowed ? "an" : "aus"}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Prod Shell</span>
              <span className="font-mono text-jarvis-text">{workerSandbox?.directProdShellAllowed ? "an" : "aus"}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Policy</span>
              <span className="truncate font-mono text-jarvis-text">{workerSandbox?.policyVersion ?? "lade..."}</span>
            </div>
          </div>
        </section>
      </div>
    </aside>
  );
}

export function SimoTWorkspaceView({ onOpenAgentOs }: SimoTWorkspaceViewProps): JSX.Element {
  const [worktrees, setWorktrees] = useState<WorktreeRecord[]>([]);
  const [codeSessions, setCodeSessions] = useState<CodexCodeWorkflowSessionView[]>([]);
  const [workerSandbox, setWorkerSandbox] = useState<WorkerSandboxStatusView | null>(null);
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [worktreeResult, sessionsResult, sandboxResult, healthResult] = await Promise.all([
        api.listWorktrees(),
        api.listCodexCodeWorkflowSessions(),
        api.workerSandboxStatus(),
        api.health(),
      ]);
      setWorktrees(worktreeResult.items);
      setCodeSessions(sessionsResult.sessions);
      setWorkerSandbox(sandboxResult);
      setHealth(healthResult);
    } catch (err) {
      setError(simoTWorkspaceErrorCopy(err instanceof Error ? err : "workspace_status_failed", "status"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  return (
    <div className="grid h-full min-h-0 min-w-0 overflow-hidden grid-rows-[minmax(0,1fr)_280px] sm:grid-rows-[minmax(0,1fr)_320px] lg:grid-cols-[minmax(0,1fr)_360px] lg:grid-rows-1">
      <div className="min-h-0 min-w-0">
        <ChatView assistantName="SimoT" />
      </div>
      <SimoTWorkspaceRail
        worktrees={worktrees}
        codeSessions={codeSessions}
        workerSandbox={workerSandbox}
        health={health}
        loading={loading}
        error={error}
        onRefresh={refresh}
        onOpenAgentOs={onOpenAgentOs}
      />
    </div>
  );
}
